import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LibraryIcon } from '../../bombiix-icon';
import { NewInput } from '../PIM/common/NewInput';
import LibraryPanel from './library-panel';
import type { LibraryTitleFieldProps } from './library-title-field.types';
import TitleVisibilityButton from './title-visibility-button';

/**
 * Title input of a section, sub-section or table with the library icon at its end. The icon opens the
 * Add to Library / Pull From Library panel below the input, the same panel a field row opens.
 * @param value - The title.
 * @param onChange - Receives the edited title.
 * @param placeholder - Input placeholder.
 * @param controlType - The library control type the item is saved and searched under.
 * @param content - The item Add to Library saves.
 * @param onPull - Receives the item pulled from the library.
 * @param isTitleShown - Whether the title is shown on the rendered form.
 * @param onTitleShownChange - When given, shows the show/hide title toggle and receives its new state.
 * @returns The title input and, while open, the library panel.
 */
function LibraryTitleField({ value, onChange, placeholder, controlType, content, onPull, isTitleShown = true, onTitleShownChange }: LibraryTitleFieldProps) {
  const { t } = useTranslation('custom-module-system-canvas');
  const [isLibraryOpen, setIsLibraryOpen] = useState(false);

  const handlePull = (pulled: unknown) => {
    onPull(pulled);
    setIsLibraryOpen(false);
  };

  return (
    <div className="cm-library-title-field cm-library-title-input--inline">
      <NewInput
        placeholder={placeholder}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        endAdornment={
          <>
            {onTitleShownChange ? (
              <>
                <span className="cm-inline-library__divider" aria-hidden="true" />
                <TitleVisibilityButton isShown={isTitleShown} onChange={onTitleShownChange} />
              </>
            ) : (
              <span className="cm-inline-library__divider" aria-hidden="true" />
            )}
            <button
              type="button"
              className={['cm-inline-library__button', isLibraryOpen ? 'cm-field-entry__library--active' : ''].filter(Boolean).join(' ')}
              aria-label={t('CUSTOM_MODULE.SYSTEM_CANVAS.SAVE_TO_LIBRARY')}
              aria-pressed={isLibraryOpen}
              onClick={() => setIsLibraryOpen((open) => !open)}
            >
              <LibraryIcon size={LIBRARY_ICON_SIZE} />
            </button>
          </>
        }
      />
      {isLibraryOpen ? <LibraryPanel controlType={controlType} content={content} onPull={handlePull} /> : null}
    </div>
  );
}

export default LibraryTitleField;

const LIBRARY_ICON_SIZE = 20;
